import React, { useState } from 'react';
import { Dropdown, Skeleton, Spin, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';

import { DropdownItem, HeaderItem, StageCard, StateLabel, Stage as StyledStage } from './styled';
import ArrowIcon from '../common/icons/ArrowIcon';
import ArrowRightIcon from '../common/icons/ArrowRightIcon';
import { canShowStationStage, canShowStationStatus, getStatusColorAndText, getColorByStageOrder } from './utils';
import FavouriteStar from '../FavouriteStar/FavouriteStar';

interface CurrentStageCardComponentProps {
  selectedStageInDropdown?: { id: number; name: string; order: number };
  currentStage?: { id: number; name: string; order: number };
  currentStatus?: { id: number; name: string; order: number };
  stages: { id: number; name: string }[];
  handleStageChange: (stageId: number) => void;
  isLoading: boolean;
  stationId?: string;
  isTransparent?: boolean;
}

const CurrentStageCardComponent: React.FC<CurrentStageCardComponentProps> = observer(
  ({
    selectedStageInDropdown,
    currentStage,
    currentStatus,
    stages,
    handleStageChange,
    isLoading,
    stationId,
    isTransparent,
  }) => {
    const { permissionStore, stationStore, usersStore } = useStores();
    const { t } = useTranslation();
    const [isSavingFavourite, setIsSavingFavourite] = useState(false);

    const { color, text } = getStatusColorAndText(stationStore.stationControlType, currentStatus);

    const onToggleFavouriteStation = async () => {
      setIsSavingFavourite(true);
      await stationStore.toggleFavouriteStation(stationId);
      setIsSavingFavourite(false);
    };

    return (
      <StageCard $isTransparent={isTransparent}>
        <div className="station-stage-space">
          <Skeleton
            active
            loading={isLoading}
            paragraph={false}
            title={{ width: '250px' }}
            style={{ width: 'fit-content' }}
          >
            <div className="station-stages-container">
              {isSavingFavourite ? (
                <Spin className="favourite-star-spinner" />
              ) : (
                <Tooltip
                  title={stationStore.isFavouriteStation(stationId) ? t('removeFromFavorite') : t('addToFavorite')}
                  placement="right"
                >
                  <FavouriteStar
                    isFavourite={stationStore.isFavouriteStation(stationId)}
                    size="small"
                    onToggle={onToggleFavouriteStation}
                    disabled={usersStore.isUpdating}
                  />
                </Tooltip>
              )}
              {canShowStationStage(permissionStore) && (
                <div className="station-stage">
                  <StateLabel>{t('stationStageState')}</StateLabel>
                  <StyledStage color={getColorByStageOrder(currentStage?.order || 0) as string}>
                    {t(currentStage?.name) || t('stationStageNoStageSelected')}
                  </StyledStage>
                  {permissionStore.isAuth(permissions.UPDATE_STATION_STAGE) && (
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        selectable: true,
                        selectedKeys: [`${selectedStageInDropdown?.id}`],
                        onClick: ({ key }) => handleStageChange(Number(key)),
                        items: [
                          {
                            key: 'stationStatus',
                            label: t('stationStageStatus'),
                            disabled: true,
                          },
                          ...stages.map(stage => ({
                            key: stage.id,
                            label: (
                              <DropdownItem>
                                {t(stage.name)}
                                <ArrowRightIcon />
                              </DropdownItem>
                            ),
                          })),
                        ],
                      }}
                    >
                      <HeaderItem>
                        {t('stationStageMoveTo')}
                        <ArrowIcon viewBox="0 0 14 14" />
                      </HeaderItem>
                    </Dropdown>
                  )}
                </div>
              )}
            </div>
          </Skeleton>

          <Skeleton
            active
            loading={isLoading}
            paragraph={false}
            title={{ width: '250px' }}
            style={{ width: 'fit-content' }}
          >
            {canShowStationStatus(stationStore, currentStatus) && (
              <Tag color={color} className="station-status-tag">
                {t(text)}
              </Tag>
            )}
          </Skeleton>
        </div>
      </StageCard>
    );
  },
);

export default CurrentStageCardComponent;
